import type { ReactNode } from "react";
import { Brightness4, Brightness7, Logout, RssFeed } from "@mui/icons-material";
import {
  AppBar,
  Chip,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Container,
  Avatar,
  Tooltip,
  alpha,
} from "@mui/material";
import { useAuth } from "../auth/useAuth";
import { getConfig } from "../config";

const ENV_COLORS: Record<string, { bg: string; text: string }> = {
  sandbox: { bg: "#FFF3E0", text: "#E65100" },
  dev:     { bg: "#E3F2FD", text: "#0D47A1" },
  qa:      { bg: "#F3E5F5", text: "#6A1B9A" },
  staging: { bg: "#FFF8E1", text: "#F57F17" },
  prod:    { bg: "#FFEBEE", text: "#C62828" },
};

export type AppShellProps = {
  children: ReactNode;
  darkMode: boolean;
  onToggleDarkMode: () => void;
};

function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] ?? ""}${parts[parts.length - 1][0] ?? ""}`.toUpperCase();
}

export default function AppShell({ children, darkMode, onToggleDarkMode }: AppShellProps) {
  const ENV_NAME = getConfig().envName;
  const { user, logout } = useAuth();
  const userName = user?.name ?? user?.email ?? "User";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <AppBar
        position="sticky"
        elevation={0}
        sx={(theme) => ({
          background:
            theme.palette.mode === "light"
              ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, #004999 100%)`
              : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.9)} 0%, ${theme.palette.secondary.main} 100%)`,
          borderBottom: `1px solid ${alpha("#fff", 0.1)}`,
        })}
      >
        <Toolbar sx={{ gap: 1.5, py: 0.5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              mr: 1,
            }}
          >
            <Box
              sx={(theme) => ({
                width: 38,
                height: 38,
                borderRadius: "10px",
                background: alpha("#fff", 0.15),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backdropFilter: "blur(8px)",
                border: `1px solid ${alpha("#fff", 0.2)}`,
                boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.15)}`,
              })}
            >
              <RssFeed sx={{ fontSize: 22, color: "#fff" }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="subtitle1"
                component="div"
                sx={{ fontWeight: 700, lineHeight: 1.2, letterSpacing: "0.02em" }}
              >
                SMRT Link
              </Typography>
              <Typography
                variant="caption"
                sx={{ opacity: 0.8, lineHeight: 1.2, fontSize: "0.7rem", letterSpacing: "0.05em", textTransform: "uppercase" }}
              >
                News Feed Admin
              </Typography>
            </Box>
          </Box>

          {ENV_NAME && (
            <Chip
              label={ENV_NAME.toUpperCase()}
              size="small"
              sx={{
                fontWeight: 800,
                fontSize: "0.7rem",
                letterSpacing: "0.08em",
                height: 26,
                borderRadius: "6px",
                bgcolor: ENV_COLORS[ENV_NAME.toLowerCase()]?.bg ?? alpha("#fff", 0.2),
                color: ENV_COLORS[ENV_NAME.toLowerCase()]?.text ?? "#fff",
                border: `1.5px solid ${ENV_COLORS[ENV_NAME.toLowerCase()]?.text ?? alpha("#fff", 0.4)}`,
                "& .MuiChip-label": { px: 1.5 },
              }}
            />
          )}

          <Box sx={{ flexGrow: 1 }} />

          <Tooltip title={darkMode ? "Switch to light mode" : "Switch to dark mode"}>
            <IconButton
              onClick={onToggleDarkMode}
              aria-label="toggle theme"
              sx={{
                color: "inherit",
                bgcolor: alpha("#fff", 0.08),
                "&:hover": { bgcolor: alpha("#fff", 0.16) },
              }}
            >
              {darkMode ? <Brightness7 fontSize="small" /> : <Brightness4 fontSize="small" />}
            </IconButton>
          </Tooltip>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1.5,
              py: 0.5,
              borderRadius: "20px",
              bgcolor: alpha("#fff", 0.08),
              maxWidth: 220,
            }}
          >
            <Avatar
              sx={{
                width: 30,
                height: 30,
                bgcolor: alpha("#fff", 0.2),
                color: "#fff",
                fontSize: "0.75rem",
                fontWeight: 700,
              }}
            >
              {initialsFromName(userName)}
            </Avatar>
            <Typography
              variant="body2"
              noWrap
              sx={{ display: { xs: "none", sm: "block" }, fontWeight: 500, fontSize: "0.8rem" }}
            >
              {userName}
            </Typography>
          </Box>

          <Tooltip title="Log out">
            <IconButton
              onClick={() => void logout()}
              aria-label="log out"
              sx={{
                color: "inherit",
                bgcolor: alpha("#fff", 0.08),
                "&:hover": { bgcolor: alpha("#fff", 0.16) },
              }}
            >
              <Logout fontSize="small" />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>
      <Box component="main" sx={{ flex: 1, py: 3 }}>
        <Container maxWidth="xl">{children}</Container>
      </Box>
    </Box>
  );
}
